import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Loader2, CheckCircle, AlertCircle, Send, CreditCard, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TRANSLATIONS = {
  summary: {
    en: "Price Summary", it: "Riepilogo Prezzo", es: "Resumen de Precio", fr: "Résumé du Prix", de: "Preisübersicht", zh: "价格摘要", ru: "Итоговая стоимость", ar: "ملخص السعر", ko: "가격 요약"
  },
  accommodation: {
    en: "Accommodation", it: "Soggiorno", es: "Alojamiento", fr: "Hébergement", de: "Unterkunft", zh: "住宿", ru: "Проживание", ar: "الإقامة", ko: "숙박"
  },
  cleaningFee: {
    en: "Cleaning fee", it: "Pulizie finali", es: "Limpieza", fr: "Ménage", de: "Reinigung", zh: "清洁费", ru: "Уборка", ar: "رسوم التنظيف", ko: "청소비"
  },
  touristTax: {
    en: "Tourist tax", it: "Tassa di soggiorno", es: "Tasa turística", fr: "Taxe de séjour", de: "Kurtaxe", zh: "旅游税", ru: "Туристический налог", ar: "الضريبة السياحية", ko: "관광세"
  },
  total: {
    en: "Total", it: "Totale", es: "Total", fr: "Total", de: "Gesamt", zh: "总计", ru: "Итого", ar: "المجموع", ko: "총액"
  },
  perNight: {
    en: "per night", it: "a notte", es: "por noche", fr: "par nuit", de: "pro Nacht", zh: "每晚", ru: "за ночь", ar: "لليلة", ko: "1박"
  },
  nights: {
    en: "nights", it: "notti", es: "noches", fr: "nuits", de: "Nächte", zh: "晚", ru: "ночей", ar: "ليالي", ko: "박"
  },
  adults: {
    en: "adults", it: "adulti", es: "adultos", fr: "adultes", de: "Erwachsene", zh: "成人", ru: "взрослых", ar: "بالغون", ko: "성인"
  },
  children: {
    en: "children", it: "bambini", es: "niños", fr: "enfants", de: "Kinder", zh: "儿童", ru: "детей", ar: "أطفال", ko: "어린이"
  },
  infants: {
    en: "infants", it: "neonati", es: "bebés", fr: "bébés", de: "Babys", zh: "婴儿", ru: "младенцев", ar: "رضع", ko: "유아"
  },
  selectDates: {
    en: "Select dates to see price", it: "Seleziona le date per vedere il prezzo", es: "Selecciona fechas para ver el precio", fr: "Sélectionnez les dates pour voir le prix", de: "Daten auswählen um Preis zu sehen", zh: "选择日期查看价格", ru: "Выберите даты для просмотра цены", ar: "اختر التواريخ لرؤية السعر", ko: "가격을 보려면 날짜를 선택하세요"
  },
  sendMessage: {
    en: "Send Message", it: "Invia Messaggio", es: "Enviar Mensaje", fr: "Envoyer Message", de: "Nachricht Senden", zh: "发送消息", ru: "Отправить сообщение", ar: "إرسال رسالة", ko: "메시지 보내기"
  },
  bookNow: {
    en: "Book Now", it: "Prenota Ora", es: "Reservar Ahora", fr: "Réserver Maintenant", de: "Jetzt Buchen", zh: "立即预订", ru: "Забронировать", ar: "احجز الآن", ko: "지금 예약"
  },
  securePayment: {
    en: "Secure payment with Stripe", it: "Pagamento sicuro con Stripe", es: "Pago seguro con Stripe", fr: "Paiement sécurisé avec Stripe", de: "Sichere Zahlung mit Stripe", zh: "使用Stripe安全支付", ru: "Безопасная оплата через Stripe", ar: "دفع آمن عبر Stripe", ko: "Stripe로 안전한 결제"
  },
  orSendMessage: {
    en: "or send a message", it: "oppure invia un messaggio", es: "o enviar un mensaje", fr: "ou envoyer un message", de: "oder Nachricht senden", zh: "或发送消息", ru: "или отправить сообщение", ar: "أو إرسال رسالة", ko: "또는 메시지 보내기"
  },
  sending: {
    en: "Sending...", it: "Invio...", es: "Enviando...", fr: "Envoi...", de: "Senden...", zh: "发送中...", ru: "Отправка...", ar: "جاري الإرسال...", ko: "전송 중..."
  },
  processing: {
    en: "Processing...", it: "Elaborazione...", es: "Procesando...", fr: "Traitement...", de: "Verarbeitung...", zh: "处理中...", ru: "Обработка...", ar: "جاري المعالجة...", ko: "처리 중..."
  },
  success: {
    en: "Message sent! We'll contact you soon.", 
    it: "Messaggio inviato! Ti contatteremo presto.", 
    es: "¡Mensaje enviado! Te contactaremos pronto.",
    fr: "Message envoyé! Nous vous contacterons bientôt.",
    de: "Nachricht gesendet! Wir melden uns bald.",
    zh: "消息已发送！我们会尽快联系您。",
    ru: "Сообщение отправлено! Мы свяжемся с вами в ближайшее время.",
    ar: "تم إرسال الرسالة! سنتواصل معك قريباً.",
    ko: "메시지가 전송되었습니다! 곧 연락드리겠습니다."
  },
  name: {
    en: "Full Name", it: "Nome Completo", es: "Nombre Completo", fr: "Nom Complet", de: "Vollständiger Name", zh: "全名", ru: "Полное имя", ar: "الاسم الكامل", ko: "성함"
  },
  email: {
    en: "Email", it: "Email", es: "Correo", fr: "Email", de: "Email", zh: "邮箱", ru: "Email", ar: "البريد الإلكتروني", ko: "이메일"
  },
  phone: {
    en: "Phone", it: "Telefono", es: "Teléfono", fr: "Téléphone", de: "Telefon", zh: "电话", ru: "Телефон", ar: "الهاتف", ko: "전화번호"
  },
  phonePrefix: {
    en: "Prefix", it: "Prefisso", es: "Prefijo", fr: "Préfixe", de: "Vorwahl", zh: "区号", ru: "Код", ar: "الرمز", ko: "국가코드"
  },
  phoneNumber: {
    en: "Phone number", it: "Numero di telefono", es: "Número de teléfono", fr: "Numéro de téléphone", de: "Telefonnummer", zh: "电话号码", ru: "Номер телефона", ar: "رقم الهاتف", ko: "전화번호"
  },
  phoneRequired: {
    en: "Phone number is required", it: "Il numero di telefono è obbligatorio", es: "El número de teléfono es obligatorio", fr: "Le numéro de téléphone est obligatoire", de: "Telefonnummer ist erforderlich", zh: "电话号码是必填项", ru: "Номер телефона обязателен", ar: "رقم الهاتف مطلوب", ko: "전화번호는 필수입니다"
  },
  message: {
    en: "Message (optional)", it: "Messaggio (opzionale)", es: "Mensaje (opcional)", fr: "Message (optionnel)", de: "Nachricht (optional)", zh: "留言（可选）", ru: "Сообщение (необязательно)", ar: "رسالة (اختياري)", ko: "메시지 (선택사항)"
  },
  childrenAges: {
    en: "Children's ages (e.g., 3, 7, 12)", it: "Età dei bambini (es. 3, 7, 12)", es: "Edades de los niños (ej. 3, 7, 12)", fr: "Âges des enfants (ex. 3, 7, 12)", de: "Alter der Kinder (z.B. 3, 7, 12)", zh: "儿童年龄（如3, 7, 12）", ru: "Возраст детей (напр. 3, 7, 12)", ar: "أعمار الأطفال (مثل 3، 7، 12)", ko: "어린이 나이 (예: 3, 7, 12)"
  },
  childAge: {
    en: "Child", it: "Bambino", es: "Niño", fr: "Enfant", de: "Kind", zh: "儿童", ru: "Ребёнок", ar: "طفل", ko: "어린이"
  },
  ageYears: {
    en: "age (years)", it: "età (anni)", es: "edad (años)", fr: "âge (ans)", de: "Alter (Jahre)", zh: "年龄（岁）", ru: "возраст (лет)", ar: "العمر (سنوات)", ko: "나이 (세)"
  },
  childrenAgesRequired: {
    en: "Please enter age for all children (min 1 year)", it: "Inserisci l'età di tutti i bambini (min 1 anno)", es: "Ingrese la edad de todos los niños (min 1 año)", fr: "Veuillez entrer l'âge de tous les enfants (min 1 an)", de: "Bitte geben Sie das Alter aller Kinder an (min 1 Jahr)", zh: "请输入所有儿童的年龄（最小1岁）", ru: "Укажите возраст всех детей (мин. 1 год)", ar: "يرجى إدخال عمر جميع الأطفال (الحد الأدنى سنة واحدة)", ko: "모든 어린이의 나이를 입력하세요 (최소 1세)"
  },
  privacy: {
    en: "I accept the", it: "Accetto la", es: "Acepto la", fr: "J'accepte la", de: "Ich akzeptiere die", zh: "我接受", ru: "Я принимаю", ar: "أقبل", ko: "동의합니다"
  },
  privacyPolicy: {
    en: "Privacy Policy", it: "Privacy Policy", es: "Política de Privacidad", fr: "Politique de Confidentialité", de: "Datenschutzerklärung", zh: "隐私政策", ru: "Политику конфиденциальности", ar: "سياسة الخصوصية", ko: "개인정보 처리방침"
  },
  error: {
    en: "Error calculating price", it: "Errore nel calcolo del prezzo", es: "Error al calcular el precio", fr: "Erreur de calcul du prix", de: "Fehler bei der Preisberechnung", zh: "价格计算错误", ru: "Ошибка расчета цены", ar: "خطأ في حساب السعر", ko: "가격 계산 오류"
  },
  paymentError: {
    en: "Payment error. Please try again.", it: "Errore di pagamento. Riprova.", es: "Error de pago. Inténtalo de nuevo.", fr: "Erreur de paiement. Veuillez réessayer.", de: "Zahlungsfehler. Bitte versuchen Sie es erneut.", zh: "支付错误。请重试。", ru: "Ошибка оплаты. Попробуйте еще раз.", ar: "خطأ في الدفع. يرجى المحاولة مرة أخرى.", ko: "결제 오류. 다시 시도해 주세요."
  },
  minNightsError: {
    en: "Minimum stay is", it: "Soggiorno minimo", es: "Estancia mínima es", fr: "Séjour minimum de", de: "Mindestaufenthalt ist", zh: "最少入住", ru: "Минимум ночей", ar: "الحد الأدنى للإقامة", ko: "최소 숙박"
  },
  maxGuestsError: {
    en: "Maximum guests:", it: "Ospiti massimi:", es: "Máximo huéspedes:", fr: "Capacité maximale:", de: "Maximale Gäste:", zh: "最多人数：", ru: "Максимум гостей:", ar: "الحد الأقصى للضيوف:", ko: "최대 인원:"
  },
  requiredField: {
    en: "This field is required", it: "Campo obbligatorio", es: "Campo obligatorio", fr: "Champ obligatoire", de: "Pflichtfeld", zh: "必填字段", ru: "Обязательное поле", ar: "حقل مطلوب", ko: "필수 항목"
  },
  privacyRequired: {
    en: "You must accept the privacy policy", it: "Devi accettare la privacy policy", es: "Debes aceptar la política de privacidad", fr: "Vous devez accepter la politique de confidentialité", de: "Sie müssen die Datenschutzerklärung akzeptieren", zh: "您必须接受隐私政策", ru: "Вы должны принять политику конфиденциальности", ar: "يجب قبول سياسة الخصوصية", ko: "개인정보 처리방침에 동의해야 합니다"
  },
  bookingDisabled: {
    en: "Online booking is currently unavailable. Please send us a message for availability.",
    it: "Le prenotazioni online non sono al momento disponibili. Inviaci un messaggio per verificare la disponibilità.",
    es: "Las reservas en línea no están disponibles actualmente. Envíenos un mensaje para consultar disponibilidad.",
    fr: "Les réservations en ligne ne sont pas disponibles actuellement. Envoyez-nous un message pour vérifier la disponibilité.",
    de: "Online-Buchungen sind derzeit nicht verfügbar. Senden Sie uns eine Nachricht zur Verfügbarkeit.",
    zh: "在线预订目前不可用。请发送消息查询可用性。",
    ru: "Онлайн-бронирование временно недоступно. Отправьте нам сообщение для уточнения доступности.",
    ar: "الحجز عبر الإنترنت غير متاح حاليًا. يرجى إرسال رسالة للاستفسار عن التوفر.",
    ko: "현재 온라인 예약이 불가능합니다. 예약 가능 여부는 메시지로 문의해 주세요."
  }
};

export default function BookingSummary({
  language = "en",
  checkIn,
  checkOut,
  adults = 2,
  children = 0,
  infants = 0,
  childrenAges: initialChildrenAges = [],
  onChildrenAgesChange,
  config = {},
  onPrivacyClick
}) {
  const [calculation, setCalculation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [bookingInProgress, setBookingInProgress] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [childrenAgesArray, setChildrenAgesArray] = useState([]);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone_prefix: "",
    phone_number: "",
    message: "",
    children_ages: "",
    privacy_accepted: false,
    honeypot: ""
  });
  const [formErrors, setFormErrors] = useState({});

  const t = useCallback((key) => TRANSLATIONS[key]?.[language] || TRANSLATIONS[key]?.en || key, [language]);
  
  // Check if online booking is enabled
  const bookingEnabled = config.booking_enabled !== false; // Default to true

  // Sync children ages from parent (BookingCalendar)
  useEffect(() => {
    if (initialChildrenAges && initialChildrenAges.length > 0) {
      setChildrenAgesArray(initialChildrenAges);
    } else if (children > 0) {
      setChildrenAgesArray(prev => {
        if (prev.length === children) return prev;
        if (prev.length < children) {
          return [...prev, ...Array(children - prev.length).fill("")];
        }
        return prev.slice(0, children);
      });
    } else {
      setChildrenAgesArray([]);
    }
  }, [children, initialChildrenAges]);

  // Update children_ages string when array changes
  useEffect(() => {
    const agesString = childrenAgesArray.filter(a => a !== "").join(", ");
    setFormData(prev => ({...prev, children_ages: agesString}));
  }, [childrenAgesArray]);

  // Calculate price when dates, guests, or children ages change
  useEffect(() => {
    if (checkIn && checkOut) {
      calculatePrice();
    } else {
      setCalculation(null);
      setError(null);
    }
  }, [checkIn, checkOut, adults, children, infants, childrenAgesArray]);

  const calculatePrice = async () => {
    setLoading(true);
    setError(null);
    try {
      // Build URL with children_ages if present
      const agesString = childrenAgesArray.filter(a => a !== "").join(",");
      let url = `${API}/booking/calculate?check_in=${checkIn}&check_out=${checkOut}&adults=${adults}&children=${children}&infants=${infants}`;
      if (agesString) {
        url += `&children_ages=${encodeURIComponent(agesString)}`;
      }
      const response = await axios.post(url);
      setCalculation(response.data);
    } catch (err) {
      setError(err.response?.data?.detail || t("error"));
      setCalculation(null);
    } finally {
      setLoading(false);
    }
  };

  // Handle child age change in summary
  const handleChildAgeChange = (index, value) => {
    const newAges = [...childrenAgesArray];
    newAges[index] = value;
    setChildrenAgesArray(newAges);
    // Notify parent if callback provided
    if (onChildrenAgesChange) {
      onChildrenAgesChange(newAges);
    }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) errors.name = t("requiredField");
    if (!formData.email.trim()) errors.email = t("requiredField");
    
    // Phone fields are now required
    if (!formData.phone_prefix.trim()) errors.phone_prefix = t("requiredField");
    if (!formData.phone_number.trim()) errors.phone_number = t("phoneRequired");
    
    // Validate children ages - all must be filled with valid values >= 1
    if (children > 0) {
      const hasInvalidAges = childrenAgesArray.some(age => !age || parseInt(age) < 1);
      if (hasInvalidAges) {
        errors.children_ages = t("childrenAgesRequired");
      }
    }
    
    if (!formData.privacy_accepted) errors.privacy = t("privacyRequired");
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // EXISTING FLOW: Send message (inquiry)
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (formData.honeypot) return; // Bot trap
    if (!validateForm()) return;
    if (!calculation) return;

    setSubmitting(true);
    try {
      await axios.post(`${API}/booking/request`, {
        name: formData.name,
        email: formData.email,
        phone_prefix: formData.phone_prefix || null,
        phone_number: formData.phone_number || null,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        children,
        children_ages: formData.children_ages || null,
        infants,
        message: formData.message || null,
        language,
        privacy_accepted: formData.privacy_accepted,
        honeypot: formData.honeypot
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Error submitting request");
    } finally {
      setSubmitting(false);
    }
  };

  // NEW FLOW: Book Now with payment
  const handleBookNow = async () => {
    if (formData.honeypot) return; // Bot trap
    if (!validateForm()) return;
    if (!calculation) return;

    setBookingInProgress(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/booking/create-checkout`, {
        name: formData.name,
        email: formData.email,
        phone_prefix: formData.phone_prefix || null,
        phone_number: formData.phone_number || null,
        check_in: checkIn,
        check_out: checkOut,
        adults,
        children,
        children_ages: formData.children_ages || null,
        infants,
        message: formData.message || null,
        language,
        privacy_accepted: formData.privacy_accepted,
        origin_url: window.location.origin
      });
      
      // Redirect to Stripe Checkout
      if (response.data.checkout_url) {
        window.location.href = response.data.checkout_url;
      } else {
        throw new Error("No checkout URL received");
      }
    } catch (err) {
      setError(err.response?.data?.detail || t("paymentError"));
      setBookingInProgress(false);
    }
  };

  const totalNights = checkIn && checkOut ? 
    Math.round((new Date(checkOut) - new Date(checkIn)) / (1000 * 60 * 60 * 24)) : 0;

  if (submitted) {
    return (
      <div className="bg-white rounded-lg shadow-lg border border-[#E7E5E4] p-8 text-center">
        <CheckCircle size={64} className="mx-auto text-green-500 mb-4" />
        <h3 className="font-heading text-2xl text-[#1C1917] mb-2">{t("success")}</h3>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow-lg border border-[#E7E5E4] overflow-hidden">
      {/* Header */}
      <div className="bg-[#1C1917] text-white p-6">
        <h3 className="font-heading text-xl">{t("summary")}</h3>
      </div>

      <div className="p-6">
        {!checkIn || !checkOut ? (
          <div className="text-center py-8 text-[#78716C]">
            <p>{t("selectDates")}</p>
          </div>
        ) : loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="animate-spin text-[#D4AF37]" size={24} />
          </div>
        ) : error ? (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle size={20} className="text-red-500 flex-shrink-0 mt-0.5" />
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        ) : calculation ? (
          <>
            {/* Stay details */}
            <div className="border-b border-[#E7E5E4] pb-4 mb-4">
              <p className="text-sm text-[#78716C]">
                {totalNights} {t("nights")} · {adults} {t("adults")}
                {children > 0 && `, ${children} ${t("children")}`}
                {infants > 0 && `, ${infants} ${t("infants")}`}
              </p>
              
              {/* Children ages editable in summary */}
              {children > 0 && (
                <div className="mt-3 p-3 bg-[#F9F8F6] rounded-lg">
                  <p className="text-xs text-[#78716C] uppercase tracking-wider mb-2">{t("childAge")} {t("ageYears")}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {childrenAgesArray.map((age, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-xs text-[#78716C]">{t("childAge")} {index + 1}:</span>
                        <select
                          value={age || ""}
                          onChange={(e) => handleChildAgeChange(index, e.target.value)}
                          className="flex-1 px-2 py-1 text-sm border border-[#E7E5E4] rounded focus:outline-none focus:ring-2 focus:ring-[#D4AF37] focus:border-transparent bg-white"
                        >
                          <option value="">--</option>
                          {Array.from({ length: 17 }, (_, i) => i + 1).map(a => (
                            <option key={a} value={a}>{a}Y</option>
                          ))}
                        </select>
                      </div>
                    ))}
                  </div>
                  {formErrors.children_ages && <p className="text-red-500 text-xs mt-2">{formErrors.children_ages}</p>}
                </div>
              )}
            </div>

            {/* Price breakdown */}
            <div className="space-y-3 mb-4">
              <div className="flex justify-between">
                <span className="text-[#78716C]">{t("accommodation")}</span>
                <span className="font-medium">€{calculation.accommodation_total.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">{t("cleaningFee")}</span>
                <span className="font-medium">€{calculation.cleaning_fee.toFixed(2)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[#78716C]">{t("touristTax")}</span>
                <span className="font-medium">€{calculation.tourist_tax_total.toFixed(2)}</span>
              </div>
            </div>

            {/* Total */}
            <div className="border-t border-[#E7E5E4] pt-4 mb-4">
              <div className="flex justify-between items-center">
                <span className="font-heading text-lg">{t("total")}</span>
                <span className="font-heading text-2xl text-[#D4AF37]">€{calculation.grand_total.toFixed(2)}</span>
              </div>
            </div>

            {/* Booking form */}
            <form onSubmit={handleSendMessage} className="space-y-4">
              {/* Honeypot */}
              <input 
                type="text" 
                name="website" 
                value={formData.honeypot}
                onChange={(e) => setFormData({...formData, honeypot: e.target.value})}
                style={{ display: "none" }}
                tabIndex={-1}
              />

              <div>
                <input
                  type="text"
                  placeholder={`${t("name")} *`}
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className={`input-luxury ${formErrors.name ? "border-red-500" : ""}`}
                />
                {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
              </div>

              <div>
                <input
                  type="email"
                  placeholder={`${t("email")} *`}
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className={`input-luxury ${formErrors.email ? "border-red-500" : ""}`}
                />
                {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
              </div>

              {/* Phone fields - inline label+placeholder style like mockup */}
              <div className="flex gap-4">
                {/* Country code input */}
                <div className="w-44 flex-shrink-0">
                  <div 
                    className="flex items-center border-b border-[rgba(28,25,23,0.2)] w-full cursor-text"
                    onClick={(e) => e.currentTarget.querySelector('input')?.focus()}
                  >
                    <span className="text-[rgba(28,25,23,0.4)] whitespace-nowrap" style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1rem' }}>Country code<span className="text-[rgba(28,25,23,0.4)]"> *</span></span>
                    <input
                      type="text"
                      placeholder="+39"
                      value={formData.phone_prefix}
                      onChange={(e) => setFormData({...formData, phone_prefix: e.target.value})}
                      onBlur={(e) => {
                        // Normalize country code on blur
                        let value = e.target.value.trim();
                        if (value) {
                          // Remove all non-digit characters except +
                          value = value.replace(/[^\d+]/g, '');
                          // Remove leading zeros (00 -> +)
                          if (value.startsWith('00')) {
                            value = '+' + value.substring(2);
                          }
                          // Add + if missing
                          if (!value.startsWith('+')) {
                            value = '+' + value;
                          }
                          // Keep only + and digits
                          value = value.replace(/[^\d+]/g, '');
                          setFormData(prev => ({...prev, phone_prefix: value}));
                        }
                      }}
                      className={`flex-1 bg-transparent outline-none min-w-0 py-4 pl-1 placeholder:text-[rgba(28,25,23,0.4)] ${formErrors.phone_prefix ? "text-red-500" : "text-[#1C1917]"}`}
                      style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1rem' }}
                      data-testid="phone-prefix-input"
                    />
                  </div>
                </div>
                {/* Phone number input */}
                <div className="flex-1">
                  <div 
                    className="flex items-center border-b border-[rgba(28,25,23,0.2)] w-full cursor-text"
                    onClick={(e) => e.currentTarget.querySelector('input')?.focus()}
                  >
                    <span className="text-[rgba(28,25,23,0.4)] whitespace-nowrap" style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1rem' }}>Phone<span className="text-[rgba(28,25,23,0.4)]"> *</span></span>
                    <input
                      type="tel"
                      placeholder="3331234567"
                      value={formData.phone_number}
                      onChange={(e) => {
                        // Allow only digits and spaces
                        const cleaned = e.target.value.replace(/[^\d\s]/g, '');
                        setFormData({...formData, phone_number: cleaned});
                      }}
                      className={`flex-1 bg-transparent outline-none min-w-0 py-4 pl-1 placeholder:text-[rgba(28,25,23,0.4)] ${formErrors.phone_number ? "text-red-500" : "text-[#1C1917]"}`}
                      style={{ fontFamily: "'Manrope', sans-serif", fontSize: '1rem' }}
                      data-testid="phone-number-input"
                    />
                  </div>
                </div>
              </div>
              {(formErrors.phone_prefix || formErrors.phone_number) && (
                <p className="text-red-500 text-xs -mt-2">{formErrors.phone_number || formErrors.phone_prefix}</p>
              )}

              <textarea
                placeholder={t("message")}
                rows={3}
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="input-luxury resize-none"
              />

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="privacy-summary"
                  checked={formData.privacy_accepted}
                  onChange={(e) => setFormData({...formData, privacy_accepted: e.target.checked})}
                  className="mt-1 w-5 h-5 accent-[#D4AF37]"
                />
                <label htmlFor="privacy-summary" className="text-sm text-[#78716C]">
                  {t("privacy")}{" "}
                  <button 
                    type="button" 
                    onClick={onPrivacyClick} 
                    className="text-[#D4AF37] underline hover:text-[#1C1917]"
                  >
                    {t("privacyPolicy")}
                  </button> *
                </label>
              </div>
              {formErrors.privacy && <p className="text-red-500 text-xs">{formErrors.privacy}</p>}

              {/* DUAL CTA BUTTONS */}
              <div className="space-y-3 pt-2">
                {/* BOOK NOW - Primary CTA (only if booking enabled) */}
                {bookingEnabled && (
                  <>
                    <button
                      type="button"
                      onClick={handleBookNow}
                      disabled={bookingInProgress || submitting}
                      className={`w-full font-semibold py-4 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                        config.book_now_button_style === "outline"
                          ? "bg-transparent border-2"
                          : "text-white"
                      }`}
                      style={config.book_now_button_style === "outline"
                        ? { 
                            borderColor: config.book_now_button_color || "#D4AF37", 
                            color: config.book_now_button_color || "#D4AF37" 
                          }
                        : { backgroundColor: config.book_now_button_color || "#D4AF37" }
                      }
                    >
                      {bookingInProgress ? (
                        <>
                          <Loader2 size={18} className="animate-spin" />
                          {t("processing")}
                        </>
                      ) : (
                        <>
                          <CreditCard size={18} />
                          {t("bookNow")}
                        </>
                      )}
                    </button>
                    
                    {/* Secure payment badge */}
                    <div className="flex items-center justify-center gap-2 text-xs text-[#78716C]">
                      <Lock size={12} />
                      <span>{t("securePayment")}</span>
                    </div>
                    
                    {/* Divider */}
                    <div className="relative py-2">
                      <div className="absolute inset-0 flex items-center">
                        <div className="w-full border-t border-[#E7E5E4]"></div>
                      </div>
                      <div className="relative flex justify-center">
                        <span className="bg-white px-4 text-xs text-[#78716C] uppercase">{t("orSendMessage")}</span>
                      </div>
                    </div>
                  </>
                )}

                {/* SEND MESSAGE - Secondary CTA (always available) */}
                <button
                  type="submit"
                  disabled={submitting || bookingInProgress}
                  className={`w-full font-semibold py-3 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                    config.send_message_button_style === "outline"
                      ? "bg-transparent border-2"
                      : "text-white"
                  }`}
                  style={config.send_message_button_style === "outline"
                    ? { 
                        borderColor: config.send_message_button_color || "#1C1917", 
                        color: config.send_message_button_color || "#1C1917" 
                      }
                    : { backgroundColor: config.send_message_button_color || "#1C1917" }
                  }
                >
                  {submitting ? (
                    <>
                      <Loader2 size={18} className="animate-spin" />
                      {t("sending")}
                    </>
                  ) : (
                    <>
                      <Send size={18} />
                      {t("sendMessage")}
                    </>
                  )}
                </button>
              </div>
            </form>
          </>
        ) : null}
      </div>
    </div>
  );
}
